<script setup lang="ts">
import { computed, watch } from 'vue';
import { useLogin } from '@/composables/use-login';
import UiModal from '@/components/ui/UiModal.vue';

const props = defineProps<{ modelValue: boolean }>();
const emit = defineEmits<{ 'update:modelValue': [value: boolean]; success: [] }>();

const {
  step,
  account,
  code,
  sending,
  submitting,
  error,
  countdown,
  codeType,
  canSend,
  canSubmit,
  sendCode,
  submit,
  reset,
} = useLogin({
  onSuccess: () => {
    emit('success');
    emit('update:modelValue', false);
  },
});

const title = computed(() => (step.value === 'account' ? '登录账号' : '输入验证码'));

watch(
  () => props.modelValue,
  (open) => {
    if (open) reset();
  },
);
</script>

<template>
  <UiModal
    :model-value="modelValue"
    :title="title"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <p class="tip">首次登录需联系客服激活账号，否则收不到验证码。</p>

    <template v-if="step === 'account'">
      <label class="field">
        <span>手机号 / 邮箱</span>
        <el-input
          v-model="account"
          placeholder="13800138000 或 name@example.com"
          autocomplete="username"
          clearable
          @keyup.enter="sendCode"
        />
      </label>
      <p class="hint">识别为{{ codeType === 'email' ? '邮箱' : '手机号' }}验证码</p>
    </template>

    <template v-else>
      <p class="account">{{ account }}</p>
      <label class="field">
        <span>验证码</span>
        <el-input
          v-model="code"
          maxlength="6"
          placeholder="4-6 位数字"
          autocomplete="one-time-code"
          @keyup.enter="submit"
        />
      </label>
      <p class="hint">开发模式验证码固定为 <strong>123456</strong></p>
    </template>

    <p v-if="error" class="error">{{ error }}</p>

    <template #footer>
      <el-button v-if="step === 'code'" @click="step = 'account'">改账号</el-button>
      <span class="spacer" />
      <el-button
        v-if="step === 'account'"
        type="primary"
        :disabled="!canSend"
        @click="sendCode"
      >
        {{ countdown > 0 ? `${countdown}s 后重发` : sending ? '发送中…' : '获取验证码' }}
      </el-button>
      <el-button v-else type="primary" :disabled="!canSubmit" @click="submit">
        {{ submitting ? '登录中…' : '登录' }}
      </el-button>
    </template>
  </UiModal>
</template>

<style scoped>
.tip {
  margin: 0;
  padding: 8px 10px;
  border-radius: 8px;
  background: var(--lg-accent-soft);
  color: var(--lg-accent);
  font-size: 12px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 13px;
  color: var(--lg-text-2);
}

.account {
  margin: 0;
  font-weight: 600;
}

.hint {
  margin: 0;
  color: var(--lg-text-2);
  font-size: 12px;
}

.error {
  margin: 0;
  color: var(--lg-color-danger);
  font-size: 13px;
}

.spacer {
  flex: 1;
}
</style>
